import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import type { AdminIdentity, AdminSessionSnapshot } from '@/shared/types/admin'
import type { AdminLoginCredentials } from '@/shared/types/admin'
import { getAdminProfile, loginAdmin, logoutAdmin } from '@/api/admin/auth'
import { writeAdminTenantCode } from '@/apps/admin/utils/tenant-preference'

import { clearAdminSession, readAdminSession, writeAdminSession } from './session-storage'

export const useAdminSessionStore = defineStore('admin-session', () => {
  const initialSession = readAdminSession()
  const token = ref(initialSession.token)
  const user = ref<AdminIdentity | null>(initialSession.user)

  const isAuthenticated = computed(() => Boolean(token.value))

  const setSession = (session: AdminSessionSnapshot): void => {
    token.value = session.token
    user.value = session.user
    writeAdminSession(session)
  }

  const setUser = (identity: AdminIdentity | null): void => {
    user.value = identity
    writeAdminSession({ token: token.value, user: identity })
  }

  const clearSession = (): void => {
    token.value = ''
    user.value = null
    clearAdminSession()
  }

  const login = async (credentials: AdminLoginCredentials): Promise<AdminIdentity> => {
    writeAdminTenantCode(credentials.tenantCode)
    const result = await loginAdmin(credentials)
    setSession({ token: result.token, user: null })

    try {
      const identity = await getAdminProfile()
      if (identity.superAdmin !== 1 && identity.tenantCode) {
        writeAdminTenantCode(identity.tenantCode)
      }
      setUser(identity)
      return identity
    } catch (error) {
      await logoutAdmin().catch(() => undefined)
      clearSession()
      throw error
    }
  }

  const logout = async (): Promise<void> => {
    try {
      if (token.value) {
        await logoutAdmin()
      }
    } finally {
      clearSession()
    }
  }

  return {
    token,
    user,
    isAuthenticated,
    setSession,
    setUser,
    clearSession,
    login,
    logout
  }
})
